import {
  faTimes,
  faSave,
  faArrowCircleLeft,
  faArrowCircleRight,
  faUndo,
  faSync,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Context as InteractionContext } from './Interaction/InteractionContext';
import { Context as InstanceDataContext } from './Positionable/InstanceDataContext';
import { Context as DataSourceContext } from './DataSourceContext';
import { useMap } from './useMap';
import { ProjectContext } from '../../contexts/ProjectContext';
import { useHistory } from 'react-router-dom';
import { MapEditorRouteContext } from "../CustomRoutes/MapEditor";
import ReactTooltip from 'react-tooltip';
import ConfirmationModal from '../Shared/ConfirmationModal/ConfirmationModal';
import AppContext from '../../contexts/AppContext';
import { toast } from 'react-toastify';
import { isLatLngInMap, latLngToPixel } from '../../utils/geo';

const GEO_REFERENCE_ROLES = [
  'Super Admin',
  'Admin',
  'Engineering Admin',
  'Regional Manager',
  'Area / Assistant Manager',
  'Divisions Admin',
];

const CALIBRATE_ROLES = [
  'Super Admin',
  'Admin',
  'Engineering Admin',
  'Regional Manager',
  'Area / Assistant Manager',
  'Divisions Admin',
  'Inspector',
  'Regional Manager (External)',
  'Client Inspector',
];

const toCoord = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);

  return Number.isFinite(n) ? n : null;
};

/** Asset has coordinates inside a georeferenced map. Math lives in `utils/geo`. */
const assetInBounds = (asset, pu) =>
  !!asset &&
  asset.latitude != null &&
  asset.longitude != null &&
  isLatLngInMap(Number(asset.latitude), Number(asset.longitude), pu);

/**
 * Initial state of options
 *
 * @typedef defaultOptions
 * @type {Array}
 */

function CancelButton(props) {
  return (
    <>
      <button className='pure-button pure-button-secondary' {...props}>
        <span>Cancel</span>
        <FontAwesomeIcon icon={faTimes} {...props} />
      </button>
    </>
  );
}

function UndoButton(props) {
  return (
    <>
      <button className='pure-button pure-button-secondary' {...props}>
        <span>Undo</span>
        <FontAwesomeIcon icon={faUndo} />
      </button>
    </>
  );
}

/**
 * Map Editor "Toolbar"
 *
 * Only "shows" the active tools, controlled by user interactions on map
 * Provides api for user to save or discard changes
 *
 * @todo separator in options
 */
export function Toolbar(props) {
  const { mapId, divisionId, projectId } = props;
  const interactionContext = useContext(InteractionContext);
  const dataContext = useContext(InstanceDataContext);
  const dc = useContext(DataSourceContext)
  const [busy, setBusy] = useState(false);
  const isTabletOrLess = useMediaQuery({ maxWidth: 768 });
  const [disableLeft, setDisableLeft] = useState(false);
  const [disableRight, setDisableRight] = useState(false);
  const [getMapId, setGetMapId] = useState(mapId);
  const [redirect, setRedirect] = useState();
  const projectContext = useContext(ProjectContext);
  const mapEditorRouteContext = useContext(MapEditorRouteContext);
  const olMap = useMap();
  const {
    mapMobileView,
    setMapMobileView,
    showGeoReference,
    setShowGeoReference,
    geoRefSaveRef,
    geoRefReady,
    gisEnabled,
    gisCalibrated,
    projectUpload,
  } = mapEditorRouteContext;
  const appContext = useContext(AppContext);
  const userRole = appContext.get('user')?.roleName;
  const canCalibrate = CALIBRATE_ROLES.includes(userRole);
  const canGeoReference = GEO_REFERENCE_ROLES.includes(userRole);
  const [filterDocs, setFilterDocs] = useState([]);
  const [isArchive, setIsArchive] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showCalibrateMap, setShowCalibrateMap] = useState(mapEditorRouteContext.showCalibrateMap || false);
  const [savingGeoRef, setSavingGeoRef] = useState(false);
  
  if (!dataContext) {
    return null;
  }

  const handleGeoReferenceClick = () => {
    const lat = toCoord(mapEditorRouteContext.project?.address?.latitude);
    const lng = toCoord(mapEditorRouteContext.project?.address?.longitude);

    if (lat === null || lng === null) {
      toast.error(
        'Project coordinates are not added. Please add a valid latitude and longitude in the project settings before georeferencing.',
      );

      return;
    }

    if (setShowGeoReference) setShowGeoReference(true);
  };

  // Check if user is on the most recent inspection
  const isRecentInsp = 
    mapEditorRouteContext.inspections?.length > 0 && 
    mapEditorRouteContext.inspections[0].id === mapEditorRouteContext.selectedInspection?.id;
  
  const selectedInspectionId = mapEditorRouteContext.selectedInspection?.id;
  const isInitialInspection = mapEditorRouteContext.inspections?.length === 1;
  const allowEditDueToSpecialInspection = !(selectedInspectionId) || isInitialInspection;

  const onDiscard = () => {
    dataContext.discard();
    interactionContext.setPositionableType(null);
  };

  const onUndo = () => {
    dataContext.undo();
  };

  // --- Asset auto-placement (geo-calibrated maps) ---------------------------
  const selectedAsset =
    interactionContext.positionableType === 'asset_type' &&
    interactionContext.positionableSourceModel != null
      ? dc.get('asset_type')?.[interactionContext.positionableSourceModel]
      : null;
  const canAutoPlace = !!(
    selectedAsset &&
    gisCalibrated &&
    projectUpload?.geo_top_left_lat != null
  );
  const autoPlaceInBounds =
    canAutoPlace && assetInBounds(selectedAsset, projectUpload);

  const onAutoPlace = () => {
    if (!autoPlaceInBounds || !olMap) return;
    const extent = olMap.getView().getProjection().getExtent();
    const imgW = extent[2];
    const imgH = extent[3];

    if (!imgW || !imgH) return;
    const coordinate = latLngToPixel(
      Number(selectedAsset.latitude),
      Number(selectedAsset.longitude),
      imgW,
      imgH,
      projectUpload,
    );

    if (!coordinate) return;
    const index = interactionContext.positionableSourceModel;

    // Re-select the asset with an auto-draw map event so the existing draw
    // pipeline places it; the null toggle forces the Draw effect to re-run.
    interactionContext.setPositionableSourceModel(null);
    setTimeout(
      () =>
        interactionContext.setPositionableSourceModel(index, {
          mapEvent: { coordinate },
        }),
      0,
    );
  };

  const handleSaveClick = () => {
    // If user is on recent inspection, save directly without confirmation
    if (isRecentInsp || allowEditDueToSpecialInspection) {
      onSave();
    } else {
      // Otherwise, show confirmation modal
      setShowSaveConfirmation(true);
    }
  };
  
  const onSave = () => {
    setShowSaveConfirmation(false);
    setBusy(true);
    interactionContext.setDefaultMode();

    dataContext.save().then(
      () => {
        interactionContext.setPositionableSourceModel(null);
        setBusy(false);
      },
      (err) => {
        setBusy(false);

        return Promise.reject(err);
      },
    );
  };

  useEffect(() => {
    let filteredArr = []
    projectContext.projectDocs && projectContext.projectDocs.documentTypes
      .filter((ele) => ele.name === 'Active Site Maps')[0]
      .documents.filter((e) => {
        const getArchiveText = window.location.href.split('/')
        const archiveIndex = getArchiveText.length-1
        
        if(getArchiveText[archiveIndex].endsWith("archive")){
          if(e.archived_at){
            filteredArr.push(e.GUID)
            setFilterDocs(filteredArr)
            setIsArchive(true)
          }
        }
       else{
         if(!e.archived_at){
           filteredArr.push(e.GUID)
           setFilterDocs(filteredArr)
          }
       }

        return e
      });

      let counter = filteredArr.indexOf(mapId);

    if (counter === 0) {
      setDisableLeft(true);
    }else{
      setDisableLeft(false);
    }

    if (counter === filteredArr.length - 1) {
      setDisableRight(true);
    }else{
      setDisableRight(false);
    }


  }, [projectContext.projectDocs]);

  // Sync showCalibrateMap state with context
  useEffect(() => {
    if (mapEditorRouteContext.showCalibrateMap !== undefined) {
      setShowCalibrateMap(mapEditorRouteContext.showCalibrateMap);
    }
  }, [mapEditorRouteContext.showCalibrateMap]);
  
  const toggleMap = (name) => {
    let counter = filterDocs.indexOf(mapId);

    if (name === 'toggleLeft') {
      if (counter === 0) {
        setRedirect(false);
      } else {
        setRedirect(true);
        setGetMapId(filterDocs[counter - 1]);
      }
    }
    if (name === 'toggleRight') {
      if (counter === filterDocs.length - 1) {
        setRedirect(false);
      } else {
        setRedirect(true);
        setGetMapId(filterDocs[counter + 1]);
      }
    }
  };

  const getUrl = useHistory();
  const getStr = getUrl.location.search;
  const testReg = getStr.match('active')

  if (redirect === true) {
    const ext = testReg !== null ? 'active-map' : 'inspection'
    const divId = divisionId || localStorage.getItem("divisionId")
    const isPublic = window.location.pathname.startsWith("/public")

    /**
     * SW2-2347 — carry the open version to the next map.
     *
     * These arrows navigate with window.location.href, a full page load, so every piece
     * of React state goes with it — including which version was open. The editor then
     * re-initialises on the newest version, which reads as "the arrow reset my version".
     *
     * Paging through a project at a point in time is the whole reason to press these
     * while on an older version, and it was impossible. The date travels in the URL
     * because that is the only thing that survives a reload; the next map picks the
     * newest version it has on or before that day, which is the same carry-forward rule
     * the server uses to decide what was on a map that day.
     *
     * Gated on being on an older version, and NOT on selectedVersionDate alone — that is
     * set for every selection including the newest. Carrying it unconditionally sends a
     * user who is simply paging through current maps to an older version of the next one,
     * because maps have independent histories and the newest day of one is an old day of
     * another. That version is read-only and back-dates saves: worse than the reset this
     * exists to fix.
     */
    const openVersionDate =
      mapEditorRouteContext?.isLatestMapVersion === false
        ? mapEditorRouteContext?.selectedVersionDate
        : null;
    const carryVersion = openVersionDate
      ? `&versionDate=${encodeURIComponent(openVersionDate)}`
      : '';

    window.location.href = isPublic
      ? `/public/divisions/${divId}/projects/${projectId}/map/${getMapId}/edit?from=${ext}${isArchive ? '&archive' : ''}${carryVersion}`
      : `/divisions/${divId}/projects/${projectId}/map/${getMapId}/edit?from=${ext}${isArchive ? '&archive' : ''}${carryVersion}`;
  }

  // GeoReference mode — only Save + Cancel visible
  if (showGeoReference) {
    return (
      <div className='map-editor-toolbar' style={{ width: '100%' }}>
        <div style={{ justifyContent: 'flex-start' }}>
          Pan & Zoom to align the map with the satellite image, then click "Save
          GeoReferencing"
        </div>
        <div
          className='map-editor-toolbar-actions'
          style={{ justifyContent: 'flex-end' }}
        >
          <CancelButton
            onClick={() => setShowGeoReference && setShowGeoReference(false)}
            disabled={savingGeoRef}
          />
          <button
            className='pure-button pure-button-primary'
            disabled={savingGeoRef || !geoRefReady}
            onClick={async () => {
              if (!geoRefSaveRef?.current || savingGeoRef || !geoRefReady) return;
              setSavingGeoRef(true);
              try {
                await geoRefSaveRef.current();
              } finally {
                // If save failed (no redirect/reload), reset so the user can retry.
                setSavingGeoRef(false);
              }
            }}
          >
            <span>{savingGeoRef ? 'Saving…' : 'Save GeoReferencing'} </span>
            <FontAwesomeIcon className='map-editor-save-icon' icon={faSave} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        'map-editor-toolbar'
      }
      style={{ justifyContent: 'space-between' }}
    >
      <div
        className={'map-editor-Angle'}
        onClick={() => toggleMap('toggleLeft')}
      >
        {disableLeft ? null : <FontAwesomeIcon icon={faArrowCircleLeft} />}
      </div>

      {dataContext.hasPendingUpdates && (
        <div className='map-editor-toolbar-actions'>
          <CancelButton onClick={onDiscard} />

          <button
            className='pure-button pure-button-primary'
            onClick={handleSaveClick}
            disabled={busy}
          >
            <span>Save</span>
            <FontAwesomeIcon
              className='map-editor-save-icon'
              icon={faSave}
            />
          </button>
          {dc.undo.length ? (
            <UndoButton onClick={onUndo} />
          ) : null}
        </div>
      )}

      {!dataContext.hasPendingUpdates && dc.undo.length ? (
        <>
          <div className='map-editor-toolbar-actions'>
            <UndoButton onClick={onUndo} />
          </div>
        </>
      ) : null}

      {canAutoPlace && (
        <div className='map-editor-toolbar-actions'>
          <button
            className='pure-button pure-button-primary'
            onClick={onAutoPlace}
            disabled={!autoPlaceInBounds}
            title={
              autoPlaceInBounds
                ? 'This map is geo-calibrated, so this asset can be auto-placed at its coordinates.'
                : "This asset's coordinates fall outside of the map."
            }
          >
            <span>Auto place Asset</span>
          </button>
        </div>
      )}

      {!dataContext.hasPendingUpdates &&
        interactionContext.mode === 'modify' && (
          <div className='map-editor-toolbar-actions'>
            <CancelButton onClick={onDiscard} isTabletOrLess={isTabletOrLess} />
          </div>
        )}

      <div className="map-editor-toolbar__right-most">
        {gisEnabled && !gisCalibrated && !isTabletOrLess && (
          <span style={{
            backgroundColor: '#FF4C51',
            color: 'white',
            padding: '4px 10px',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 600,
            marginRight: 8,
          }}>
            GIS is enabled but not configured. Please Configure First
          </span>
        )}
        { isTabletOrLess ? 
          <></> :
          <>
            {!mapMobileView && canGeoReference && gisEnabled && (
              <span
                onClick={handleGeoReferenceClick}
                className="map-editor-toolbar__view-button"
                style={{ backgroundColor: 'inherit' }}
              >
                GeoReference Map
              </span>
            )}
            {!mapMobileView && canCalibrate && (
              <span
                onClick={() => {
                  const newShowCalibrateMap = !showCalibrateMap;
                  setShowCalibrateMap(newShowCalibrateMap);
                  if (mapEditorRouteContext.setShowCalibrateMap) {
                    mapEditorRouteContext.setShowCalibrateMap(
                      newShowCalibrateMap,
                    );
                  }
                }} 
                className="map-editor-toolbar__view-button"
                style={{
                  backgroundColor: showCalibrateMap || mapEditorRouteContext.showCalibrateMap ? '#002230' : 'inherit',
                }}
              >
                Calibrate Map
              </span>
            )}
<span onClick={() => setMapMobileView(!mapMobileView)} className="map-editor-toolbar__view-button">{mapMobileView ? "Desktop View" : "Mobile View" }</span>
          </>
        }

        <div
          className={'map-editor-Angle'}
          onClick={() => toggleMap('toggleRight')}
        >
          {disableRight ? null : <FontAwesomeIcon icon={faArrowCircleRight} />}
        </div>
      </div>

      <ConfirmationModal
        show={showSaveConfirmation}
        handleConfirm={onSave}
        handleClose={() => setShowSaveConfirmation(false)}
        title='Are you sure you want to save these changes?'
        subTitle={`You're making changes to an older version of this site map. Once saved, any legend items you add cannot be edited, unless done in the most recent version of the map. Do you wish to continue?`}
        buttonText='Yes, Save Changes'
      />
    </div>
  );
}

export default Toolbar;